/**
 * The Profil tab: who the trainer is to their clients, how they sign in, and the two ways out.
 *
 * Three groups of rows in macOS System Settings' arrangement (owner-approved polish, 4 November
 * 2026): „Profil" (photograph, first name, surname and the save), „Logowanie" (the address and the
 * password behind a disclosure), and a last group holding „Wyloguj się" and „Usuń konto". Drawn in
 * the sheet's `.tight` cut, because this tab has to fit a 1280x720 laptop with no scroll.
 *
 * It owns no state: everything arrives on one object from `lib/useAccountProfile`, whose read also
 * feeds the card in the column beside it, and which holds the disclosure's open state too — this
 * tab remounts on every switch and the drafts it shows do not.
 *
 * A refused control is `aria-disabled`, never the attribute: it has to stay in the tab order for
 * its reason to be reachable (view ADR §12), and the reason is RENDERED and pointed at with
 * `aria-describedby` rather than hidden in a `title`.
 */

import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { PasswordCriteria } from '~/features/auth-by-email';
import { BrandLoader } from '~/shared/ui/BrandLoader';
import { LockIcon } from '~/shared/ui/AuthIcons';
import { FormBanner } from '~/shared/ui/FormBanner';
import { FormField } from '~/shared/ui/FormField';
import { PasswordField } from '~/shared/ui/PasswordField';
import { displayInitials } from '~/shared/lib/displayInitials';
import photoBox from '~/shared/ui/photoBox.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { AccountProfileApi } from '../lib/useAccountProfile';
import { AVATAR_ACCEPT } from '../lib/avatarFile';
import { PRIMARY_PILL, SECONDARY_PILL, DANGER_PILL } from './accountButtons';
import { SignOutButton } from './SignOutButton';
import screen from './AccountScreen.module.css';
import styles from './ProfileTab.module.css';

/** The four rules, in the mockup's order, against upstream's own words. */
const RULES = [
  { key: 'length', labelKey: 'auth.pw.len' },
  { key: 'uppercase', labelKey: 'auth.pw.upper' },
  { key: 'digit', labelKey: 'auth.pw.num' },
  { key: 'special', labelKey: 'auth.pw.spec' },
] as const satisfies readonly { key: keyof PasswordCriteria; labelKey: string }[];

interface ProfileTabProps {
  readonly profile: AccountProfileApi;
  /** Opens the erase-account dialog. */
  readonly onRequestErase: () => void;
  /**
   * The screen holds this so the dialog can hand focus BACK to the control that raised it. The
   * button is still mounted when the dialog closes, so this is the ordinary return rather than a
   * repair — but naming it here is what keeps it that way if the row ever moves.
   */
  readonly eraseRef: React.RefObject<HTMLButtonElement | null>;
}

export function ProfileTab({ profile, onRequestErase, eraseRef }: ProfileTabProps) {
  const { t } = useTranslation();
  const saveRefusalId = useId();
  const passwordRefusalId = useId();
  const passwordPanelId = useId();
  const firstPasswordField = useRef<HTMLInputElement>(null);
  const passwordOpener = useRef<HTMLButtonElement>(null);
  /* Seeded with the state the tab MOUNTS in, so a remount onto an open disclosure takes no focus:
   * focus moves only on the trainer's own open, cancel or successful save. */
  const wasOpen = useRef(profile.passwordOpen);

  useEffect(() => {
    if (profile.passwordOpen && !wasOpen.current) firstPasswordField.current?.focus();
    else if (!profile.passwordOpen && wasOpen.current) passwordOpener.current?.focus();
    wasOpen.current = profile.passwordOpen;
  }, [profile.passwordOpen]);

  // The READ, deliberately, and NOT the section's gated `waiting`: this group draws no skeleton
  // bars — the fields' own boxes are the reserve — so there is nothing here that outlives the
  // answer. `aria-busy`, the hidden sentence and `readOnly` all settle when the values arrive.
  const reading = profile.state === 'pending';
  const failed = profile.state === 'error';

  return (
    <div className={`${String(screen.inner)} ${String(screen.tight)}`}>
      <p className={`${String(screen.ledeText)} ${String(screen.lede)}`}>
        {t('web:account.profile.lede')}
      </p>

      <h2 className={screen.groupLabel}>{t('web:account.profile.heading')}</h2>
      {/* No skeleton bars in this group, and that is the loading policy honoured rather than
        * skipped: a form's boxes ARE the reserve. The rows are laid out identically in all three
        * states and only their contents arrive. */}
      <div className={screen.group} aria-busy={reading}>
        {reading && <span className={hidden.srOnly}>{t('web:account.profile.waiting')}</span>}

        <div className={screen.groupRow}>
          <span className={screen.rowLabel}>{t('web:account.profile.photoLabel')}</span>
          <div className={`${String(screen.rowBody)} ${String(styles.photoBody)}`}>
            {/* THREE branches. While the photograph is being prepared and sent, the circle itself
              * is what waits; the busy state does not end until `/auth/me` has answered, so the
              * loader hands over to the photograph and never to the initials in between. */}
            {profile.photoBusy ? (
              <span className={`${String(styles.avatar)} ${String(styles.avatarBusy)}`}>
                <BrandLoader label={t('web:account.photo.uploading')} size="panel" />
              </span>
            ) : profile.avatarUrl === null ? (
              <span className={styles.avatar} aria-hidden="true">
                {displayInitials(profile.initials)}
              </span>
            ) : (
              <img
                className={`${String(photoBox.photo)} ${String(styles.photo)}`}
                src={profile.avatarUrl}
                alt=""
                decoding="async"
              />
            )}
            <span>{t('web:account.profile.photoNote')}</span>
          </div>
          <div className={screen.rowActions}>
            {/* Offered only when there are prepared bytes to send again — a network failure must
              * not make the trainer find the file a second time. */}
            {profile.onRetryPhoto !== null && (
              <button type="button" className={SECONDARY_PILL} onClick={profile.onRetryPhoto}>
                {t('web:account.photo.retry')}
              </button>
            )}
            {profile.avatarUrl !== null && (
              <button
                type="button"
                className={SECONDARY_PILL}
                aria-label={t('web:account.photo.remove')}
                aria-disabled={profile.photoBusy ? 'true' : undefined}
                onClick={profile.photoBusy ? undefined : profile.onRemovePhoto}
              >
                {t('web:account.photo.removeShort')}
              </button>
            )}
            {/* The label IS the control's box and the input inside it is the control: the platform's
              * own picker opens, and the tab order needs nothing held in place by JavaScript. */}
            <label className={`${SECONDARY_PILL} ${String(styles.pick)}`}>
              <span aria-hidden="true">{t('web:account.photo.pick')}</span>
              <span className={hidden.srOnly}>{t('web:account.photo.change')}</span>
              <input
                className={styles.fileInput}
                type="file"
                accept={AVATAR_ACCEPT}
                // Refused, never `disabled`: a disabled input leaves the tab order, so the one
                // keyboard path to the photograph disappears for the length of every upload.
                aria-disabled={profile.photoBusy ? 'true' : undefined}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Cleared so that picking the SAME file twice still fires a change event.
                  event.target.value = '';
                  if (file !== undefined && !profile.photoBusy) profile.onPickPhoto(file);
                }}
              />
            </label>
          </div>
        </div>

        {/* The visible name is the row's; the field carries the same words as its own label, drawn
          * off-screen, so the input is named once and the row's text is not read twice. */}
        <div className={screen.groupRow}>
          <span className={screen.rowLabel} aria-hidden="true">
            {t('web:account.profile.firstName')}
          </span>
          <div className={screen.rowBody}>
            <FormField
              inline
              label={t('web:account.profile.firstName')}
              value={profile.firstName}
              onChange={profile.onFirstNameChange}
              maxLength={50}
              autoComplete="given-name"
              // `readOnly`, never `disabled`: a disabled field leaves the tab order while the read
              // is in flight. The group carries the `aria-busy` and the reason.
              readOnly={reading || failed}
            />
          </div>
        </div>

        <div className={screen.groupRow}>
          <span className={screen.rowLabel} aria-hidden="true">
            {t('web:account.profile.lastName')}
          </span>
          <div className={screen.rowBody}>
            <FormField
              inline
              label={t('web:account.profile.lastName')}
              value={profile.lastName}
              onChange={profile.onLastNameChange}
              maxLength={50}
              autoComplete="family-name"
              readOnly={reading || failed}
            />
          </div>
        </div>

        <div className={screen.groupRow}>
          <div className={screen.rowActions}>
            {!profile.saveIsOffered && (
              <span id={saveRefusalId} className={hidden.srOnly}>
                {t('web:account.profile.saveRefusal')}
              </span>
            )}
            <button
              type="button"
              className={PRIMARY_PILL}
              aria-disabled={profile.saveIsOffered ? undefined : 'true'}
              aria-describedby={profile.saveIsOffered ? undefined : saveRefusalId}
              onClick={profile.saveIsOffered ? profile.onSave : undefined}
            >
              {t('web:account.profile.save')}
            </button>
          </div>
        </div>
      </div>
      {failed && <FormBanner message={t('web:account.profile.loadFailed')} />}
      <FormBanner message={profile.photoError} />
      <FormBanner message={profile.saveError} />

      <h2 className={screen.groupLabel}>{t('web:account.profile.signInGroup')}</h2>
      <div className={screen.group}>
        <div className={screen.groupRow}>
          <span className={screen.rowLabel}>{t('web:account.profile.email')}</span>
          {/* Text, not a read-only field: the backend refuses `email` outright
            * (`email_change_unsupported`), and the address is still selectable and copyable.
            * Masked from session replay by hand: Clarity masks inputs on its own, and a span is not
            * one (the Google account's address on Integracje is masked the same way). */}
          <div className={screen.rowBody}>
            <span className={styles.email} data-clarity-mask="True">
              {profile.email}
              <LockIcon className={styles.lock} />
            </span>
            <span className={styles.note}>{t('web:account.profile.emailHint')}</span>
          </div>
        </div>

        <div className={screen.groupRow}>
          <span className={screen.rowLabel}>{t('web:account.password.heading')}</span>
          <div className={screen.rowBody}>
            <span className={styles.mask} aria-hidden="true">
              {t('web:account.password.mask')}
            </span>
          </div>
          {!profile.passwordOpen && (
            <div className={screen.rowActions}>
              <button
                ref={passwordOpener}
                type="button"
                className={SECONDARY_PILL}
                aria-expanded={false}
                aria-controls={passwordPanelId}
                onClick={profile.onOpenPassword}
              >
                {t('web:account.password.change')}
              </button>
            </div>
          )}
        </div>

        {profile.passwordOpen && (
          <div className={screen.groupRow}>
          <div id={passwordPanelId} className={styles.passwordPanel}>
            <div className={styles.pair}>
              <PasswordField
                label={t('web:account.password.new')}
                value={profile.newPassword}
                onChange={profile.onNewPasswordChange}
                autoComplete="new-password"
                inputRef={firstPasswordField}
              />
              <PasswordField
                label={t('web:account.password.repeat')}
                value={profile.repeatPassword}
                onChange={profile.onRepeatPasswordChange}
                autoComplete="new-password"
                // The ring with no words of its own: the reason is the rules beside the button.
                invalid={profile.passwordAssessment.confirmation === 'mismatch'}
              />
            </div>

            <div className={styles.passwordFoot}>
              <ul className={styles.rules}>
                {RULES.map((rule) => {
                  const met = profile.passwordAssessment.criteria[rule.key];
                  return (
                    <li
                      key={rule.key}
                      className={`${String(styles.rule)} ${met ? String(styles.ruleMet) : ''}`}
                      // The state reaches assistive technology through the ROLE: the platform
                      // announces "checked" in the reader's own language.
                      role="checkbox"
                      aria-checked={met}
                      aria-disabled="true"
                    >
                      <span className={styles.ruleMark} aria-hidden="true" />
                      {t(rule.labelKey)}
                    </li>
                  );
                })}
              </ul>
              {!profile.passwordSaveIsOffered && (
                <span id={passwordRefusalId} className={hidden.srOnly}>
                  {t('web:account.password.refusal')}
                </span>
              )}
              <button type="button" className={SECONDARY_PILL} onClick={profile.onCancelPassword}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className={PRIMARY_PILL}
                aria-disabled={profile.passwordSaveIsOffered ? undefined : 'true'}
                aria-describedby={profile.passwordSaveIsOffered ? undefined : passwordRefusalId}
                onClick={profile.passwordSaveIsOffered ? profile.onSavePassword : undefined}
              >
                {t('web:account.password.save')}
              </button>
            </div>
            <FormBanner message={profile.passwordError} />
          </div>
          </div>
        )}
      </div>

      <div className={screen.group}>
        <div className={screen.groupRow}>
          <span className={screen.rowLabel}>{t('web:account.signOut.action')}</span>
          <span className={screen.rowBody}>{t('web:account.signOut.confirmBody')}</span>
          <div className={screen.rowActions}>
            <SignOutButton />
          </div>
        </div>
        <div className={screen.groupRow}>
          <span className={`${String(screen.rowLabel)} ${String(screen.rowLabelDanger)}`}>
            {t('web:account.profile.erase')}
          </span>
          <span className={screen.rowBody}>{t('web:account.profile.eraseNote')}</span>
          <div className={screen.rowActions}>
            <button
              ref={eraseRef}
              type="button"
              className={DANGER_PILL}
              onClick={onRequestErase}
            >
              {t('web:account.profile.eraseAction')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
